import React, { useEffect, useState } from 'react';
import { api } from './auth';

const RAND = (c) => `R${((c || 0) / 100).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const LABELS = {
  tokenise_attempt: 'Card saved (attempt)',
  tokenise_success: 'Card saved',
  tokenise_failed: 'Card save failed',
  auth_attempt: 'Auth (attempt)',
  auth_success: 'Auth succeeded',
  auth_failed: 'Auth failed',
  charge_attempt: 'Charge (attempt)',
  charge_success: 'Charge succeeded',
  charge_failed: 'Charge failed',
  refund_attempt: 'Refund (attempt)',
  refund_success: 'Refund',
  refund_failed: 'Refund failed',
  extension_redeemed: 'Extension code redeemed',
};

function statusColour(t) {
  if (t.endsWith('success')) return { bg: '#ECFDF5', fg: '#047857' };
  if (t.endsWith('failed')) return { bg: '#FEF2F2', fg: '#DC2626' };
  return { bg: '#F3F4F6', fg: '#6B7280' };
}

export default function AdminPayments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eventType, setEventType] = useState('');
  const [days, setDays] = useState(90);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (eventType) params.set('event_type', eventType);
    params.set('days', String(days));
    api.get(`/admin/payments?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [eventType, days]);

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return iso;
    }
  };

  return (
    <div style={card}>
      <ExtensionCodesPanel />

      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Payments and reconciliation</h2>
      <p style={{ color: '#6B7280', fontSize: 13, marginTop: 4 }}>
        Every payment event across all companies. Match the reference column against your Peach dashboard to reconcile. This view is read only.
      </p>

      {data && data.summary && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '18px 0' }}>
          <Stat label="Collected (30 days)" value={RAND(data.summary.collected_30d_cents)} />
          <Stat label="Successful charges (30 days)" value={data.summary.successful_charges_30d} />
          <Stat label="Failed charges (30 days)" value={data.summary.failed_charges_30d} accent={data.summary.failed_charges_30d ? '#DC2626' : undefined} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <select value={eventType} onChange={(e) => setEventType(e.target.value)} style={sel}>
          <option value="">All event types</option>
          <option value="charge_success">Charge succeeded</option>
          <option value="charge_failed">Charge failed</option>
          <option value="tokenise_success">Card saved</option>
          <option value="tokenise_failed">Card save failed</option>
        </select>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={sel}>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last 12 months</option>
        </select>
      </div>

      {loading ? (
        <p style={{ color: '#9CA3AF' }}>Loading…</p>
      ) : !data || !data.events || data.events.length === 0 ? (
        <p style={{ color: '#9CA3AF' }}>No payment events in this window.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {['Date', 'Company', 'Event', 'Amount', 'Card', 'Reference', 'Code'].map((h) => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.events.map((e) => {
                const c = statusColour(e.event_type);
                return (
                  <tr key={e.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                    <td style={td}>{fmt(e.created_at)}</td>
                    <td style={td}>{e.company_name}</td>
                    <td style={td}>
                      <span style={{ background: c.bg, color: c.fg, borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>
                        {LABELS[e.event_type] || e.event_type}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>{e.amount_cents ? RAND(e.amount_cents) : '.'}</td>
                    <td style={td}>{e.card_brand ? `${e.card_brand} ****${e.card_last4 || ''}` : '.'}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{e.processor_reference || '.'}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{e.result_code || '.'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div style={{ flex: '1 1 160px', minWidth: 160, background: '#F8F9FB', border: '1px solid #E5E7EB', borderRadius: 12, padding: '14px 16px' }}>
      <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: accent || '#111827' }}>{value}</div>
    </div>
  );
}

const card = { background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '20px 24px' };
const th = { textAlign: 'left', padding: '8px 10px', color: '#6B7280', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', color: '#374151', whiteSpace: 'nowrap' };
const sel = { padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' };

// ── v5.6: extension codes (operator-issued, single-use) ─────────────────

function ExtensionCodesPanel() {
  const [open, setOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [codes, setCodes] = useState([]);
  const [days, setDays] = useState(30);
  const [companyId, setCompanyId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [freshCode, setFreshCode] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const [c, k] = await Promise.all([
        api.get('/admin/companies'),
        api.get('/admin/billing/extension-codes'),
      ]);
      setCompanies(c.data || []);
      setCodes(k.data || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load extension codes');
    }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const generate = async () => {
    setErr(null); setFreshCode(null); setCopied(false); setBusy(true);
    try {
      const payload = { days: Number(days) || 30 };
      if (companyId) payload.company_id = Number(companyId);
      if (note.trim()) payload.note = note.trim();
      const r = await api.post('/admin/billing/extension-codes', payload);
      setFreshCode(r.data);
      setNote('');
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(typeof detail === 'string' ? detail : (detail?.message || e.message || 'Could not generate the code'));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(freshCode.code); setCopied(true); } catch (e) { /* clipboard blocked, code is visible to copy manually */ }
  };

  const codeStatus = (c) => {
    if (c.redeemed) return { label: 'REDEEMED', bg: '#DCFCE7', fg: '#166534' };
    if (c.expires_at && new Date(c.expires_at) < new Date()) return { label: 'EXPIRED', bg: '#F3F4F6', fg: '#6B7280' };
    return { label: 'AVAILABLE', bg: '#EFF6FF', fg: '#1D4ED8' };
  };

  const companyName = (id) => {
    if (!id) return 'Any company';
    const c = companies.find(x => x.id === id);
    return c ? c.name : `Company #${id}`;
  };

  return (
    <div style={{ marginBottom: 20, border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }}>
      <button onClick={() => setOpen(!open)} style={{ width: '100%', textAlign: 'left', background: '#F8F9FB', border: 'none', padding: '14px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>Extension codes</span>
        <span style={{ fontSize: 12, color: '#6B7280' }}>{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <div style={{ padding: '16px' }}>
          <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 14px', lineHeight: 1.5 }}>
            Single-use codes that extend a company's no-charge window. The customer redeems the code on the
            trial-ended screen, and no payment runs before the extended date. Codes are shown once at generation,
            only a hash is stored, and every redemption is logged in the payments feed below.
          </p>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
            <div>
              <label style={lbl}>Days to extend</label>
              <input type="number" min={1} max={365} value={days} onChange={e => setDays(e.target.value)} style={{ ...sel, width: 90 }} />
            </div>
            <div>
              <label style={lbl}>Bind to company (optional)</label>
              <select value={companyId} onChange={e => setCompanyId(e.target.value)} style={{ ...sel, minWidth: 200 }}>
                <option value="">Any company</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={lbl}>Note (why issued)</label>
              <input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. White glove onboarding" style={{ ...sel, width: '100%', boxSizing: 'border-box' }} />
            </div>
            <button onClick={generate} disabled={busy} style={{ background: busy ? '#9CA3AF' : '#111827', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 600, fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer' }}>
              {busy ? 'Generating…' : 'Generate code'}
            </button>
          </div>

          {err && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 12px', color: '#DC2626', fontSize: 12, marginBottom: 12 }}>{String(err)}</div>}

          {freshCode && (
            <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                Copy this code now, it will not be shown again
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <code style={{ fontSize: 18, fontWeight: 700, letterSpacing: 1, color: '#111827', background: '#fff', border: '1px solid #BBF7D0', borderRadius: 8, padding: '6px 12px' }}>{freshCode.code}</code>
                <button onClick={copy} style={{ background: '#fff', border: '1px solid #D1D5DB', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  {copied ? 'Copied' : 'Copy'}
                </button>
                <span style={{ fontSize: 12, color: '#374151' }}>
                  {freshCode.days} days, {companyName(freshCode.company_id)}, code valid until {new Date(freshCode.expires_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          )}

          {codes.length === 0 ? (
            <p style={{ fontSize: 12, color: '#9CA3AF', margin: 0 }}>No codes issued yet.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={th}>Code</th><th style={th}>Days</th><th style={th}>Company</th>
                  <th style={th}>Note</th><th style={th}>Status</th><th style={th}>Issued</th><th style={th}>Redeemed by</th>
                </tr></thead>
                <tbody>
                  {codes.map(c => {
                    const s = codeStatus(c);
                    return (
                      <tr key={c.id} style={{ borderTop: '0.5px solid #E5E7EB' }}>
                        <td style={{ ...td, fontFamily: 'monospace' }}>{c.code_masked}</td>
                        <td style={td}>{c.days}</td>
                        <td style={td}>{companyName(c.company_id)}</td>
                        <td style={{ ...td, whiteSpace: 'normal', maxWidth: 220 }}>{c.note || '-'}</td>
                        <td style={td}>
                          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 700, background: s.bg, color: s.fg }}>{s.label}</span>
                        </td>
                        <td style={td}>{new Date(c.created_at).toLocaleDateString()}</td>
                        <td style={td}>{c.redeemed ? `${c.redeemed_by || ''} (${new Date(c.redeemed_at).toLocaleDateString()})` : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const lbl = { fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 4 };
